import { useEffect, useState } from 'react';
import { I18n } from '@aws-amplify/core';
import { useApplicationContext } from '@/context/v1/Application/context';
import { useGetListsActionPlan, useGetPreventiveMeasure } from '@/hooks/v2/resolution-886';
import { useResolution886Report } from '@/context/Resolution886Report';
import { Resolution886ActionPlanTask } from '@/constants/Resolution886';

interface GeneralPreventiveMeasures {
	number: number;
	measure: string;
	response: string;
	observation: string;
}

interface ActionPlanReview {
	number: number;
	actionPlanName: string;
	administrativeMeasure: string;
	administrativeDueDate: string;
	engineeringMeasure: string;
	engineeringDueDate: string;
	riskLevel?: string;
}

interface PreventiveMeasureData {
	number: number;
	measure: string;
	type: string;
}

export function useGetActionPlanReview() {
	const { organization, company } = useApplicationContext();
	const { previousReview, report } = useResolution886Report();

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [limit] = useState<number>(10);
	const [previousMCP, setPreviousMCP] = useState<GeneralPreventiveMeasures[]>([]);
	const [previousActionPlanData, setPreviousActionPlanData] = useState<ActionPlanReview[]>([]);

	const { data: previousPreventiveMeasures } = useGetPreventiveMeasure({
		organization_id: organization?.id!,
		company_id: company?.id!,
		review_id: (previousReview && previousReview?.id!) || '',
		report_id: report?.id!
	});

	const { data: previousActionPlan } = useGetListsActionPlan({
		organization_id: organization?.id!,
		company_id: company?.id!,
		review_id: (previousReview && previousReview?.id!) || '',
		report_id: report?.id!,
		limit: 100
	});

	function setGeneralPreventiveMeasuresDataReview(data: PreventiveMeasureData): GeneralPreventiveMeasures {
		const { number, measure, type } = data;

		const item = previousPreventiveMeasures?.[0]?.preventive_measure_results?.find(
			(item: any) => item.preventive_measure.key === type
		);

		return {
			number,
			measure: I18n.get(measure),
			response: item!.value === 'NO' ? I18n.get('No') : I18n.get('Yes'),
			observation: item!.description
		};
	}

	function setActionPlanDataReview(data: any): ActionPlanReview[] {
		const result = [];

		for (let item = 0; item < data.length; item++) {
			const { title, tasks } = data[item].category_result.action_plan;

			const administrativeTask = tasks?.find((task: any) => task.type?.includes(Resolution886ActionPlanTask.ADMINISTRATIVE));
			const engineeringTask = tasks?.find((task: any) => task.type?.includes(Resolution886ActionPlanTask.ENGINEERING));

			result.push({
				number: item + 1,
				actionPlanName: title,
				administrativeMeasure: administrativeTask?.title || 'N/A',
				administrativeDueDate: administrativeTask?.due_date,
				engineeringMeasure: engineeringTask?.title || 'N/A',
				engineeringDueDate: engineeringTask?.due_date,
				riskLevel: ''
			});
		}

		return result;
	}

	const generalPreventiveMeasuresDataReview: PreventiveMeasureData[] = [
		{
			number: 1,
			measure: I18n.get(
				'The worker(s), supervisor(s), engineer(s), and management related to the workstation have been informed about the risks associated with the task of developing MSD'
			),
			type: 'risk_information'
		},
		{
			number: 2,
			measure:
				'The worker(s) and supervisor(s) related to the workstation have been trained on identifying symptoms related to the development of MSD',
			type: 'symptom_training'
		},
		{
			number: 3,
			measure:
				'The worker(s) and supervisor(s) related to the workstation have been trained on the measures and/or procedures to prevent the development of MSD',
			type: 'symptom_identification'
		}
	];

	const startIndex = (currentPage - 1) * limit;
	const endIndex = startIndex + limit;
	const paginatedActionPlanData = previousActionPlanData.slice(startIndex, endIndex);

	useEffect(() => {
		if (previousPreventiveMeasures && previousPreventiveMeasures.length) {
			const data = generalPreventiveMeasuresDataReview.map((item) =>
				setGeneralPreventiveMeasuresDataReview(item)
			);

			setPreviousMCP(data);
		}
	}, [previousPreventiveMeasures]);

	useEffect(() => {
		if (previousActionPlan && previousActionPlan.rows.length) {
			const data = setActionPlanDataReview(previousActionPlan.rows);

			setPreviousActionPlanData(data);
		}
	}, [previousActionPlan]);

	return {
		limit,
		currentPage,
		previousMCP,
		previousActionPlanData,
		paginatedActionPlanData,
		setCurrentPage
	};
}
